/*
 * 通用请求封装（axios）
 * - 基础配置：baseURL、timeout、headers
 * - 拦截器：携带 token、统一错误处理、自动解析服务端返回结构
 * - 方法别名：get/post/put/delete/patch
 * - 额外能力：文件上传/下载、取消请求
 * - 增强：configureRequest 环境初始化、语言头部、uniapp风格 code 兼容
 */

import axios from "axios";
import moment from "moment/moment";
import COS from "cos-js-sdk-v5";
import OSS from "ali-oss";
import { ElMessage } from "element-plus";

// 默认环境与语言映射（对齐示例）
const defaultConfig = {
  // 生产环境
  host: "https://app.akkogear.com/",
  // 测试/开发
  devHost: "https://app.akkogear.com/",
  // 模拟
  mockHost: "https://apifoxmock.com/m1/5713030-0-default/",
};

const langMap = {
  "zh-Hans": "zh_CN",
  zh: "zh_CN",
  en: "en_US",
};

// 基础配置
const instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL || "/api",
  timeout: 20000,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "Access-Control-Allow-Origin": "*",
  },
});

// 外部初始化入口（可选）
export function configureRequest(options = {}) {
  const mode =
    options.mode || (process.env.NODE_ENV === "development" ? "dev" : "prod");
  if (mode === "mock") instance.defaults.baseURL = defaultConfig.mockHost;
  else if (mode === "dev") instance.defaults.baseURL = defaultConfig.devHost;
  else instance.defaults.baseURL = defaultConfig.host;

  instance.defaults.timeout = options.timeout || instance.defaults.timeout;
  instance.defaults.withCredentials =
    options.withCredentials ?? instance.defaults.withCredentials;

  instance.__getToken =
    typeof options.getToken === "function"
      ? options.getToken
      : () => localStorage.getItem("token");
  instance.__getLang =
    typeof options.getLang === "function"
      ? options.getLang
      : () => {
        const saved = localStorage.getItem("lang");
        const nav = navigator.language || "zh";
        return langMap[saved] || langMap[nav] || "zh_CN";
      };
  instance.__onLogout =
    typeof options.onLogout === "function" ? options.onLogout : null;
}

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    const token = instance.__getToken
      ? instance.__getToken()
      : localStorage.getItem("token");
    if (token) config.headers.token = token;

    // 附带语言
    const lang = instance.__getLang
      ? instance.__getLang()
      : langMap[navigator.language] || "zh_CN";
    config.headers.lang = lang;

    return config;
  },
  (error) => Promise.reject(error)
);

// 统一错误映射
function normalizeError(error) {
  if (axios.isCancel(error)) {
    return { code: "CANCELED", message: "请求已取消", raw: error };
  }
  const status = error?.response?.status;
  const message =
    error?.response?.data?.message || error?.message || "网络异常，请稍后重试";
  return { code: status || "ERROR", message, raw: error };
}

// 响应拦截器（兼容 uniapp 风格 code）
instance.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data && typeof data === "object" && "code" in data) {
      if (data.code === -2) {
        // 登录失效，可触发登出钩子
        instance.__onLogout && instance.__onLogout();
        return Promise.reject({
          code: -2,
          message: data.message || "登录状态失效",
          raw: response,
        });
      }
      if (data.code < 0) {
        // 频率限制友好提示
        if (data.msg === "Rate limit exceeded") {
          try {
            ElMessage.warning("您的操作过于频繁！");
          } catch (e) {
            console.log(e);
          }
        }
        return Promise.reject({
          code: data.code,
          message: data.message || data.msg || "请求失败",
          raw: response,
        });
      }
      // 成功：0 或 200
      if (data.code === 0 || data.code === 200) return data.data;
    }
    console.log(data, "---");
    return data;
  },
  (error) => Promise.reject(normalizeError(error))
);

// 常用方法封装
// export const request = (config) => instance(config);
export const get = (url, params = {}, config = {}) =>
  instance({ url, method: "get", params, ...config });
export const post = (url, data = {}, config = {}) =>
  instance({ url, method: "post", data, ...config });
export const put = (url, data = {}, config = {}) =>
  instance({ url, method: "put", data, ...config });
export const del = (url, params = {}, config = {}) =>
  instance({ url, method: "delete", params, ...config });
export const patch = (url, data = {}, config = {}) =>
  instance({ url, method: "patch", data, ...config });

export const request = {
  get,
  post,
  put,
  del,
  patch,
};

// 文件上传（multipart/form-data）
export const upload = (url, fileOrFormData, extra = {}, config = {}) => {
  const form =
    fileOrFormData instanceof FormData ? fileOrFormData : new FormData();
  if (!(fileOrFormData instanceof FormData)) {
    form.append(extra.fieldName || "file", fileOrFormData);
  }
  Object.entries(extra.fields || {}).forEach(([k, v]) => form.append(k, v));
  return instance({
    url,
    method: "post",
    data: form,
    headers: { "Content-Type": "multipart/form-data" },
    ...config,
  });
};

// 文件下载（支持 blob）
export const download = async (
  url,
  params = {},
  filename = "download",
  config = {}
) => {
  const blob = await instance({
    url,
    method: "get",
    params,
    responseType: "blob",
    ...config,
  });
  const a = document.createElement("a");
  const href = window.URL.createObjectURL(blob);
  a.href = href;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(href);
};

/**
 * 上传文件
 * @param {object} options
 */
export const uploadFile = (options) => {
  const path = options.path;
  const file = options.file;
  const onPercent = options.onProgress;
  const onSuccess = options.onSuccess;
  const onFail = options.onFail;
  try {
    let credentials, url;
    request.get('api/file/getSecretKey').then((res) => {
      if (res.oss_type === 'ALIYUN') {
        const client = new OSS({
          // yourRegion填写Bucket所在地域。以华东1（杭州）为例，Region填写为oss-cn-hangzhou。
          region: res.region,
          // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
          accessKeyId: res.accessKeyId,
          accessKeySecret: res.accessKeySecret,
          // 从STS服务获取的安全令牌（SecurityToken）。
          stsToken: res.securityToken,
          // 刷新临时访问凭证的时间间隔，单位为毫秒。
          refreshSTSTokenInterval: 300000,
          // 填写Bucket名称。
          bucket: res.bucket
        });
        const options = {
          mime: "json",
          headers: { "Content-Type": "text/plain" },
        };
        client.put(handleUploadName("img", path, file.name), file.raw, options).then((uploadRes) => {
          // 创建一个 URL 对象
          const urlObj = new URL(uploadRes.url);
          // 从 URL 对象中获取路径部分
          const url = res.host + urlObj.pathname.substring(1);
          // TODO: url处理
          // request.get('admin/file/getAccessUrl', { url }).then(res => {
          //   console.log(res)
          //   onSuccess({ url: res })
          // })
          onSuccess?.({ url })
        }).catch((e) => {
          onFail?.(e)
        });
      } else {
        credentials = res.credentials;
        let cos = new COS({
          getAuthorization: function (options, callback) {
            callback({
              TmpSecretId: credentials.tmpSecretId,
              TmpSecretKey: credentials.tmpSecretKey,
              SecurityToken: credentials.sessionToken,
              StartTime: credentials.startTime,
              ExpiredTime: credentials.expiredTime
            });
          }
        });
        cos.uploadFile(
          {
            Bucket: credentials.bucket,
            Region: credentials.region,
            Key: handleUploadName("img", path, file.name),
            Body: file.raw,
            SliceSize: 1024 * 1024 * 5 /* 触发分块上传的阈值，超过5MB使用分块上传，小于5MB使用简单上传。可自行设置，非必须 */,
            onProgress: (progressData) => {
              onPercent?.({ file, percent: progressData.percent });
            },
            onFileFinish: (err, data, options) => {
              url = credentials.path + "/" + options.Key;
              onPercent?.({ file, url, percent: 100 });
              onSuccess?.({ url })

              // TODO: url处理
              // request.get('admin/file/getAccessUrl', { url })
              //   .then(res => {
              //     onPercent?.({ file, percent: 100 });
              //     onSuccess?.({ url: res })
              //   })
            }
          },
          function (err) {
            onFail?.(err)
          }
        );
      }
    });
  } catch (error) {
    console.log(error);
  }
}

// 规范上传图片格式
const handleUploadName = function (type, path, file_name) {
  // /img/类型（avatar/category/article）/年月日/md5(文件名+随机字符串+毫秒数)+时间.文件后缀
  let newFileName =
    type +
    '/' +
    path +
    '/' +
    moment(new Date()).format('YYMMDD') +
    '/' +
    file_name.split(file_name.substring(file_name.lastIndexOf('.')))[0] +
    Math.random().toString(36).substring(2) +
    Date.now() +
    moment(new Date()).format('HHmm') +
    file_name.substring(file_name.lastIndexOf('.'));
  return newFileName;
};

export default instance;
